import _memoize from 'lodash/memoize';
import { getFont, FONT } from '@tornado-rdk/typography';
import { PRIMARY, makeThemeColor, DEFAULT_MODE } from '@tornado-rdk/theme';
import { FULL_HD, HD, RENDERING_CONTEXT } from '@bedrock-rdk/constants';
import { identity, middle, SCREEN_WIDTH } from '../../../utils/sizes';
import { cacheResolver, makeDefaultStylingContext } from '../../../utils/style';
import { TRANSPARENT } from '../../../utils/colors';
import { imageOptions, templates } from '../../solo.constants';

export const defaultContext = makeDefaultStylingContext();

export const FLEX_CONTAINER_WIDTH = {
  [HD]: 460,
  [FULL_HD]: 688,
};

export const FLEX_CONTAINER_HEIGHT = {
  [HD]: 400,
  [FULL_HD]: 466,
};

export const FLEX_CONTAINER_PADDING_BOTTOM = {
  [HD]: 81,
  [FULL_HD]: 174,
};

export const LOGO_PADDING_BOTTOM = {
  [HD]: 16,
  [FULL_HD]: 24,
};

export const DETAILS_ROW_PADDING_BOTTOM = 16;

export const DETAILS_ROW_HEIGHT = {
  [HD]: 32,
  [FULL_HD]: 40,
};

export const SERVICE_ICON_MARGIN_RIGHT = {
  [HD]: 8,
  [FULL_HD]: 12,
};

export const DESCRIPTION_MARGIN_RIGHT = {
  [HD]: 12,
  [FULL_HD]: 16,
};

export const DESCRIPTION_MARGIN_LEFT = {
  [HD]: 8,
  [FULL_HD]: 12,
};

export const TITLE_MARGIN_TOP = 16;

export const PROGRESS_BAR_WIDTH = 356;

const TITLE_WORD_PERCENTAGE = 0.4;

export const PROGRESS_BAR_MARGIN_TOP = {
  [HD]: 24,
  [FULL_HD]: 32,
};

export const INFO_ICON = { name: 'InfoLight', type: 'icon' };

export const CTA_ROW_MARGIN_TOP = {
  [HD]: 24,
  [FULL_HD]: 32,
};

export const SERVICE_ICON_HEIGHT = {
  [HD]: 32,
  [FULL_HD]: 40,
};

export const BASIC_ICON_HEIGHT = {
  [HD]: 24,
  [FULL_HD]: 32,
};

export const CTA_HEIGHT = {
  [HD]: 56,
  [FULL_HD]: 64,
};

export const CTA_BUTTON_MARGIN_RIGHT = {
  [HD]: 24,
  [FULL_HD]: 32,
};

export const getServiceIconViewBox = _memoize(screenSize => ({
  h: SERVICE_ICON_HEIGHT[screenSize],
}));

export const getBasicIconViewBox = _memoize(screenSize => ({
  h: BASIC_ICON_HEIGHT[screenSize],
}));

export const colors = {
  text: makeThemeColor(PRIMARY),
};

export const getTitleWordWrapWidth = screenSize => Math.round(SCREEN_WIDTH[screenSize] * TITLE_WORD_PERCENTAGE);

export const makeBasicTextStyle = ({ font, colorMode }) => ({
  color: colors.text[colorMode],
  text: {
    ...font,
    verticalAlign: 'middle',
    wordWrap: true,
    maxLines: 2,
    maxLinesSuffix: '...',
  },
});

export const getInitialStyle = _memoize(
  ({ screenSize = FULL_HD, colorMode = DEFAULT_MODE } = defaultContext) => ({
    Gradient: {
      w: identity,
      h: identity,
    },
    FlexContainer: {
      rect: true,
      color: TRANSPARENT,
      w: FLEX_CONTAINER_WIDTH[screenSize],
      h: FLEX_CONTAINER_HEIGHT[screenSize],
      y:
        imageOptions[templates.Solo][RENDERING_CONTEXT.GENERAL][screenSize].PRIMARY.height -
        FLEX_CONTAINER_HEIGHT[screenSize] -
        FLEX_CONTAINER_PADDING_BOTTOM[screenSize],
      flex: {
        direction: 'column',
        justifyContent: 'flex-end',
      },
      Logo: {
        flexItem: {
          marginBottom: LOGO_PADDING_BOTTOM[screenSize],
        },
      },
      DetailsFlexContainer: {
        rect: true,
        h: DETAILS_ROW_HEIGHT[screenSize],
        color: TRANSPARENT,
        flex: {
          direction: 'row',
        },
        flexItem: {
          paddingBottom: DETAILS_ROW_PADDING_BOTTOM,
        },
        ServiceIcon: {},
        Description: {
          mountY: 0.5,
          y: middle,
          text: {
            ...getFont(FONT.BERLIN_1, screenSize),
            maxLines: 1,
            verticalAlign: 'middle',
          },
          alpha: 0.8,
          flexItem: {
            marginLeft: DESCRIPTION_MARGIN_LEFT[screenSize],
            marginRight: DESCRIPTION_MARGIN_RIGHT[screenSize],
          },
        },
        BasicIcon: {
          flexItem: {
            marginTop: (DETAILS_ROW_HEIGHT[screenSize] - BASIC_ICON_HEIGHT[screenSize]) / 2,
          },
          alpha: 0.8,
        },
      },
      Title: {
        ...makeBasicTextStyle({
          font: getFont(FONT.PARIS_3, screenSize),
          colorMode,
        }),
        flexItem: {
          marginTop: TITLE_MARGIN_TOP,
        },
      },
      ProgressBar: {
        w: PROGRESS_BAR_WIDTH,
        mountY: 1,
        flexItem: {
          marginTop: PROGRESS_BAR_MARGIN_TOP[screenSize],
        },
      },
      CTAFlexContainer: {
        rect: true,
        h: CTA_HEIGHT[screenSize],
        w: FLEX_CONTAINER_WIDTH[screenSize],
        color: TRANSPARENT,
        flex: {
          direction: 'row',
        },
        flexItem: {
          marginTop: CTA_ROW_MARGIN_TOP[screenSize],
        },
        PlayButton: {
          flexItem: {
            marginRight: CTA_BUTTON_MARGIN_RIGHT[screenSize],
          },
        },
        MyListButton: {
          flexItem: {
            marginRight: CTA_BUTTON_MARGIN_RIGHT[screenSize],
          },
        },
        InfoButton: {
          picto: INFO_ICON,
        },
      },
    },
  }),
  cacheResolver,
);
